// frontend/src/components/BottomNav.jsx
import { Home, PlusCircle, User, LogIn, Shield, Search, MessageCircle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useEffect, useState } from "react";
import api from "../config/api";

export default function BottomNav({ onSearchClick }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [unreadChats, setUnreadChats] = useState(0);

  // Detectar si es mobile al redimensionar
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Verificar si estamos en la página de inicio (Home)
  const isHomePage = location.pathname === "/";

  // 🔥 CARGAR CONTADOR DE MENSAJES NO LEÍDOS
  useEffect(() => {
    if (!user) {
      setUnreadChats(0);
      return;
    }

    const loadUnreadCount = async () => {
      try {
        const response = await api.get("/chat/unread-count");
        setUnreadChats(response.data.count || 0);
      } catch (error) {
        console.error("Error loading unread chat count:", error);
      }
    };

    loadUnreadCount();

    // Actualizar cada 30 segundos
    const interval = setInterval(loadUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [user]);

  // 🔥 ACTUALIZAR CONTADOR CUANDO CAMBIA LA RUTA
  useEffect(() => {
    if (user && location.pathname === "/chat") {
      // Cuando entra al chat, el contador se actualizará en 30 seg
      // Pero lo ponemos a 0 inmediatamente
      setTimeout(() => {
        api.get("/chat/unread-count").then((res) => {
          setUnreadChats(res.data.count || 0);
        });
      }, 1000);
    }
  }, [location.pathname, user]);

  const linkClasses = (path) =>
    `flex flex-col items-center transition-colors ${
      location.pathname === path || location.pathname.startsWith(path + "/")
        ? "text-[#449879]"
        : "text-gray-600 hover:text-[#449879]"
    }`;

  if (loading) {
    return (
      <nav className="bg-white border-t border-gray-200 fixed bottom-0 left-0 right-0 z-50 block shadow-lg">
        <div className="max-w-lg mx-auto flex justify-around items-center h-14 md:h-16">
          <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
          <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
          <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
          <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
        </div>
      </nav>
    );
  }

  // 🔥 FUNCIONES PARA PERFIL
  const getPerfilLink = () => {
    if (!user) return "/login";
    return "/perfil";
  };

  const getPerfilIcon = () => {
    if (!user) return <LogIn className="w-5 h-5" strokeWidth={2} />;
    return <User className="w-5 h-5" strokeWidth={2} />;
  };

  const getPerfilLabel = () => {
    if (!user) return "Entrar";
    return "Perfil";
  };

  const shouldShowAdmin = () => {
    return user && user.role === "admin";
  };

  // 🔥 ICONO WRAPPER CON CONTADOR OPCIONAL
  const IconWrapper = ({ icon: Icon, isActive, label, badge }) => (
    <div className="relative flex flex-col items-center">
      <div
        className={`relative transition-all duration-300 ${
          isActive ? "scale-110" : "scale-100 group-hover:scale-105"
        }`}
      >
        <Icon
          className={`w-5 h-5 transition-all duration-300 ${
            isActive
              ? "text-[#449879] drop-shadow-[0_0_8px_rgba(68,152,121,0.3)]"
              : "text-gray-600 group-hover:text-[#449879]/80"
          }`}
          strokeWidth={isActive ? 2.5 : 2}
        />
        {isActive && (
          <div className="absolute -inset-2 bg-[#449879]/5 rounded-full blur-sm animate-pulse" />
        )}
        {/* 🔥 BADGE DE NOTIFICACIONES */}
        {badge > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center animate-pulse">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </div>
      <span
        className={`text-[10px] font-medium mt-0.5 transition-all duration-300 ${
          isActive ? "text-[#449879]" : "text-gray-500 group-hover:text-[#449879]/80"
        }`}
      >
        {label}
      </span>
      {isActive && (
        <div className="w-1 h-1 rounded-full bg-[#449879] mt-0.5 animate-pulse" />
      )}
    </div>
  );

  return (
    <nav className="bg-white/80 backdrop-blur-md border-t border-gray-100/80 fixed bottom-0 left-0 right-0 z-50 block shadow-lg shadow-black/5">
      <div className="max-w-lg mx-auto flex justify-around items-center h-14 md:h-16 px-2">
        {/* HOME */}
        <Link to="/" className={linkClasses("/")}>
          <IconWrapper
            icon={Home}
            isActive={location.pathname === "/"}
            label="Início"
          />
        </Link>

        {/* BUSCAR - SOLO visible en mobile Y en la página de inicio (Home) */}
        {isMobile && isHomePage && (
          <button
            onClick={onSearchClick}
            className="flex flex-col items-center transition-colors duration-300 group"
          >
            <IconWrapper icon={Search} isActive={false} label="Buscar" />
          </button>
        )}

        {/* PUBLICAR - SOLO visible si hay usuario logueado */}
        {user && (
          <Link to="/publicar" className={linkClasses("/publicar")}>
            <IconWrapper
              icon={PlusCircle}
              isActive={location.pathname === "/publicar"}
              label="Publicar"
            />
          </Link>
        )}

        {/* 🔥 CHAT - SOLO visible si hay usuario logueado */}
        {user && (
          <Link to="/chat" className={linkClasses("/chat")}>
            <IconWrapper
              icon={MessageCircle}
              isActive={location.pathname === "/chat" || location.pathname.startsWith("/chat/")}
              label="Chat"
              badge={unreadChats}
            />
          </Link>
        )}

        {/* 🔥 PERFIL / ENTRAR - SIEMPRE */}
        <Link to={getPerfilLink()} className={linkClasses(getPerfilLink())}>
          <IconWrapper
            icon={getPerfilIcon().type}
            isActive={location.pathname === getPerfilLink()}
            label={getPerfilLabel()}
          />
        </Link>

        {/* 🔥 ADMIN - SOLO PARA ADMIN (AL FINAL) */}
        {shouldShowAdmin() && (
          <Link to="/admin" className={linkClasses("/admin")}>
            <IconWrapper
              icon={Shield}
              isActive={location.pathname === "/admin"}
              label="Admin"
            />
          </Link>
        )}
      </div>
    </nav>
  );
}